import type { SearchHistoryEntry } from "@/types/models";

const STORAGE_KEYS = {
  SHORTCUT_LINKS: "dashboard_shortcut_links",
  BG_IMAGE: "dashboard_bg_image",
  SEARCH_HISTORY: "dashboard_search_history",
  NOTES: "dashboard_notes",
  TASKS: "dashboard_tasks",
  SCHEDULES: "dashboard_schedules",
  NOTES_VISIBLE: "dashboard_notes_visible",
  TASKS_VISIBLE: "dashboard_tasks_visible",
  SCHEDULES_VISIBLE: "dashboard_schedules_visible",
  CALENDAR_VISIBLE: "dashboard_calendar_visible",
};

/**
 * ローカルストレージからの読み込み
 */
export function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * ローカルストレージへの保存
 */
export function saveToStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Failed to save to storage:", error);
  }
}

/**
 * 検索履歴の読み込み
 */
export function loadSearchHistory(): Record<string, SearchHistoryEntry> {
  return loadFromStorage(STORAGE_KEYS.SEARCH_HISTORY, {});
}

/**
 * 検索履歴の保存
 */
export function saveSearchHistory(
  history: Record<string, SearchHistoryEntry>,
): void {
  saveToStorage(STORAGE_KEYS.SEARCH_HISTORY, history);
}

/**
 * メモの読み込み
 */
export function loadNotes<T>(defaultValue: T): T {
  return loadFromStorage(STORAGE_KEYS.NOTES, defaultValue);
}

/**
 * メモの保存
 */
export function saveNotes<T>(notes: T): void {
  saveToStorage(STORAGE_KEYS.NOTES, notes);
}

/**
 * タスクの読み込み
 */
export function loadTasks<T>(defaultValue: T): T {
  return loadFromStorage(STORAGE_KEYS.TASKS, defaultValue);
}

/**
 * タスクの保存
 */
export function saveTasks<T>(tasks: T): void {
  saveToStorage(STORAGE_KEYS.TASKS, tasks);
}

/**
 * スケジュールの読み込み
 */
export function loadSchedules<T>(defaultValue: T): T {
  return loadFromStorage(STORAGE_KEYS.SCHEDULES, defaultValue);
}

/**
 * スケジュールの保存
 */
export function saveSchedules<T>(schedules: T): void {
  saveToStorage(STORAGE_KEYS.SCHEDULES, schedules);
}

/**
 * スコア計算関数（直近アクセスと頻度の加重平均）
 */
export function calculateScore(
  entry: SearchHistoryEntry,
  currentTime?: number,
): number {
  const now = currentTime || Date.now();
  const daysSinceAccess = (now - entry.lastAccess) / (1000 * 60 * 60 * 24);

  // 周期的減衰: 1.5日ごとに0.8倍に減衰
  const recencyScore = Math.max(0, 1 / Math.pow(1.5, daysSinceAccess));

  // アクセス数スコア（正規化）
  const frequencyScore = Math.min(entry.count / 10, 1);

  // 加重平均: 頻度70% + 直近性30%
  const score = frequencyScore * 0.7 + recencyScore * 0.3;

  return score;
}

/**
 * マッチする検索履歴を取得してスコアで並べ替え
 */
export function getSuggestionsFromHistory(query: string): SearchHistoryEntry[] {
  const history = loadSearchHistory();
  const lowerQuery = query.toLowerCase();

  const matches = Object.values(history).filter((entry) =>
    entry.query.toLowerCase().includes(lowerQuery),
  );

  // スコア計算して並べ替え
  matches.sort((a, b) => {
    const scoreA = calculateScore(a);
    const scoreB = calculateScore(b);
    return scoreB - scoreA;
  });

  return matches;
}

/**
 * 最後のアクセス日時をフォーマット
 */
export function formatLastAccess(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "今";
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;

  const date = new Date(timestamp);
  return date.toLocaleDateString("ja-JP");
}

/**
 * HTML エスケープ
 */
export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export { STORAGE_KEYS };

/**
 * 検索クエリを履歴に追加/更新（サーバーへ送信し、ローカルにも保存）
 */
export async function recordSearchQuery(
  query: string,
  url: string | null = null,
): Promise<void> {
  if (!query || !query.trim()) return;

  const now = Date.now();

  // try server first
  try {
    await fetch("/api/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, url }),
    });
  } catch (e) {
    // ignore server error and update local storage as fallback
    const history = loadSearchHistory();
    if (history[query]) {
      history[query].count += 1;
      history[query].lastAccess = now;
      if (url) history[query].url = url;
    } else {
      history[query] = {
        query,
        url: url || null,
        count: 1,
        lastAccess: now,
        firstAccess: now,
      };
    }
    saveSearchHistory(history);
  }
}
